import { useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import {
  Linkedin, Facebook, Instagram, FileText, Clock, Pencil, Trash2,
  CheckCircle2, XCircle, Loader2, X, Filter, Bot, Eye, AlertTriangle, Send
} from "lucide-react";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const STATUS_CONFIG = {
  brouillon: { label: "Brouillon", bg: "bg-slate-500/10 text-slate-400", icon: FileText },
  en_attente_validation: { label: "En attente", bg: "bg-amber-500/10 text-amber-400", icon: Clock },
  valide: { label: "Valide", bg: "bg-blue-500/10 text-blue-400", icon: CheckCircle2 },
  planifie: { label: "Planifie", bg: "bg-indigo-500/10 text-indigo-400", icon: Clock },
  publie: { label: "Publie", bg: "bg-green-500/10 text-green-400", icon: CheckCircle2 },
  rejete: { label: "Rejete", bg: "bg-red-500/10 text-red-400", icon: XCircle },
};

export function MarketingPostsList({ posts, api, onReload, onEditPost }) {
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [validating, setValidating] = useState(null);
  const [showValidation, setShowValidation] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showPreview, setShowPreview] = useState(null);
  const [publishing, setPublishing] = useState(null);

  const filtered = posts.filter(p => {
    if (filterPlatform && !(p.platforms || [p.platform]).includes(filterPlatform)) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    return true;
  });

  const deletePost = async (pid) => {
    if (!window.confirm("Supprimer ce post ?")) return;
    await api.delete(`/marketing/posts/${pid}`);
    onReload();
  };

  const validatePost = async (postId, action) => {
    setValidating(postId);
    try {
      await api.post(`/marketing/posts/${postId}/validate`, {
        action,
        reason: action === "reject" ? rejectReason : "",
      });
      setShowValidation(null);
      setRejectReason("");
      onReload();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
    setValidating(null);
  };

  const publishPost = async (postId) => {
    setPublishing(postId);
    try {
      const r = await api.post(`/marketing/posts/${postId}/publish`);
      const results = r.data?.results || [];
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        alert(`Publication partielle: ${failures.map(f => `${f.platform}: ${f.error}`).join(", ")}`);
      }
      onReload();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur de publication");
    }
    setPublishing(null);
  };

  return (
    <div data-testid="posts-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-1 text-[10px] text-slate-500">
          <Filter className="w-3 h-3" /> Filtres :
        </div>
        <select
          value={filterPlatform}
          onChange={e => setFilterPlatform(e.target.value)}
          className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1 text-[11px]"
          data-testid="filter-platform"
        >
          <option value="">Toutes plateformes</option>
          {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => (
            <option key={k} value={k}>{cfg.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1 text-[11px]"
          data-testid="filter-status"
        >
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_CONFIG).map(([k, cfg]) => (
            <option key={k} value={k}>{cfg.label}</option>
          ))}
        </select>
        <span className="text-[10px] text-slate-500 self-center ml-auto">
          {filtered.length} post{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Posts list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <FileText className="w-14 h-14 mx-auto mb-4 text-slate-600" />
          <p>Aucun post</p>
          <p className="text-xs mt-1">Utilisez le generateur IA pour creer vos premiers posts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const postPlatforms = p.platforms || [p.platform || "linkedin"];
            const mainPlatform = postPlatforms[0];
            const cfg = PLATFORM_CONFIG[mainPlatform] || PLATFORM_CONFIG.linkedin;
            const statusCfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.brouillon;
            const StatusIcon = statusCfg.icon;

            return (
              <div
                key={p.post_id}
                className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors"
                data-testid={`post-${p.post_id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Platform icons */}
                  <div className="flex flex-col gap-1">
                    {postPlatforms.map(pl => {
                      const plCfg = PLATFORM_CONFIG[pl] || PLATFORM_CONFIG.linkedin;
                      const PlIcon = plCfg.icon;
                      return (
                        <div key={pl} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${plCfg.color}15` }}>
                          <PlIcon className="w-4 h-4" style={{ color: plCfg.color }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      {postPlatforms.map(pl => {
                        const plCfg = PLATFORM_CONFIG[pl] || PLATFORM_CONFIG.linkedin;
                        return (
                          <span key={pl} className="text-[10px] font-bold" style={{ color: plCfg.color }}>
                            {plCfg.label}
                          </span>
                        );
                      })}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${statusCfg.bg}`}>
                        <StatusIcon className="w-2.5 h-2.5" /> {statusCfg.label}
                      </span>
                      {p.ai_generated && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981] flex items-center gap-1">
                          <Bot className="w-2.5 h-2.5" /> IA
                        </span>
                      )}
                      {p.scheduled_at && (
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(p.scheduled_at).toLocaleDateString("fr-FR")} {new Date(p.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-slate-300 whitespace-pre-wrap line-clamp-3">{p.content}</p>

                    {p.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {p.hashtags.map((h, i) => (
                          <span key={i} className="text-[9px] text-[#10b981]">#{h}</span>
                        ))}
                      </div>
                    )}

                    {/* Validation rejection info */}
                    {p.status === "rejete" && p.validation?.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-500/5 border border-red-500/10 rounded-lg">
                        <span className="text-[10px] text-red-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Motif : {p.validation.rejection_reason}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    {p.status === "en_attente_validation" && (
                      <>
                        <button
                          onClick={() => validatePost(p.post_id, "validate")}
                          className="p-1.5 hover:bg-green-500/10 rounded transition-colors"
                          title="Valider"
                          data-testid={`validate-post-${p.post_id}`}
                        >
                          {validating === p.post_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-green-400" />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                          )}
                        </button>
                        <button
                          onClick={() => { setShowValidation(p.post_id); setRejectReason(""); }}
                          className="p-1.5 hover:bg-red-500/10 rounded transition-colors"
                          title="Rejeter"
                          data-testid={`reject-post-${p.post_id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </>
                    )}
                    {["valide", "planifie", "brouillon"].includes(p.status) && (
                      <button
                        onClick={() => publishPost(p.post_id)}
                        className="p-1.5 hover:bg-[#1877f2]/10 rounded transition-colors"
                        title="Publier sur les reseaux"
                        data-testid={`publish-post-${p.post_id}`}
                      >
                        {publishing === p.post_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1877f2]" />
                        ) : (
                          <Send className="w-3.5 h-3.5 text-[#1877f2]" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setShowPreview(p)}
                      className="p-1.5 hover:bg-white/5 rounded transition-colors"
                      title="Apercu"
                    >
                      <Eye className="w-3 h-3 text-slate-400" />
                    </button>
                    <button
                      onClick={() => onEditPost(p)}
                      className="p-1.5 hover:bg-white/5 rounded transition-colors"
                      title="Modifier"
                    >
                      <Pencil className="w-3 h-3 text-slate-400" />
                    </button>
                    <button
                      onClick={() => deletePost(p.post_id)}
                      className="p-1.5 hover:bg-red-500/10 rounded transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!showValidation} onOpenChange={(v) => { if (!v) setShowValidation(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Rejeter le post</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Motif du rejet..."
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm h-24 resize-none"
              data-testid="reject-reason"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => validatePost(showValidation, "reject")}
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
                disabled={validating}
                data-testid="confirm-reject-btn"
              >
                {validating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Rejeter
              </Button>
              <Button onClick={() => setShowValidation(null)} variant="outline" className="border-white/10 text-slate-400">
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!showPreview} onOpenChange={(v) => { if (!v) setShowPreview(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Apercu du post</DialogTitle></DialogHeader>
          {showPreview && (
            <div className="mt-2 space-y-3">
              <div className="flex gap-2">
                {(showPreview.platforms || [showPreview.platform]).map(pl => {
                  const plCfg = PLATFORM_CONFIG[pl] || PLATFORM_CONFIG.linkedin;
                  const PlIcon = plCfg.icon;
                  return (
                    <span key={pl} className="flex items-center gap-1 text-xs font-bold" style={{ color: plCfg.color }}>
                      <PlIcon className="w-4 h-4" /> {plCfg.label}
                    </span>
                  );
                })}
              </div>
              <div className="bg-[#0b0811] border border-white/10 rounded-lg p-4">
                <p className="text-sm text-slate-200 whitespace-pre-wrap">{showPreview.content}</p>
                {showPreview.hashtags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-white/5">
                    {showPreview.hashtags.map((h, i) => (
                      <span key={i} className="text-xs text-[#10b981]">#{h}</span>
                    ))}
                  </div>
                )}
              </div>
              {showPreview.scheduled_at && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Planifie: {new Date(showPreview.scheduled_at).toLocaleDateString("fr-FR")} a {new Date(showPreview.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
